import ConfirmModal from '@/components/SharedComponents/ConfirmModal/ConfirmModal';
import { PatientStackParamList } from '@/navigations/PatientStack';
import { TherapistStackParamList } from '@/navigations/TherapistStack';
import { useStoreActions, useStoreState } from '@/stores/store';
import { logError } from '@/utils/logHandling';
import { StackScreenProps } from '@react-navigation/stack';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useNotification } from '@/components/ui/therapist/Notification/NotificationProvider';
import api from '../../api/axios';
import { SelectExercise } from './SelectExercise';

export function Aufgaben({
  route,
}: StackScreenProps<
  TherapistStackParamList | PatientStackParamList,
  'Aufgaben'
>) {
  const {
    exerciseClass,
    patientId,
    setId,
    isNetworkError,
    exerciseSettings,
    isSelf = false,
    selectedPatient,
    activeSets,
    preview,
  } = route.params;

  const patientIdRef = useRef(patientId);
  const resultBuffer = useStoreState(
    (state) => state.resultsWithKey['results' + patientIdRef.current],
  );
  const unloadTherapistBuffer = useStoreActions(
    (actions) => actions.unloadTherapistBuffer,
  );
  const [showModal, setShowModal] = useState(false);
  const { showNotification } = useNotification();

  const hideModal = useCallback((withToast: boolean) => {
    return () => {
      setShowModal(false);
      if (!withToast) {
        return;
      }
      showNotification({
        type: 'info',
        title: 'Ergebnisse zwischengespeichert',
        position: 'top',
      });
    };
  }, []);

  const hideModalWithToast = hideModal(true);
  const hideModalWithoutToast = hideModal(false);

  const uploadResults = useCallback(async () => {
    // used when there is an issue with the network and the therapist/patient wants to upload results later or retry upload
    api
      .post('/api/results', {
        Results: resultBuffer ?? [],
      })
      .then((result) => {
        if (result.status === 200) {
          showNotification({
            type: 'info',
            title: 'Ergebnisse hochgeladen',
            position: 'top',
          });
          unloadTherapistBuffer({ patientId: patientIdRef.current });
          hideModalWithoutToast();
          setShowModal(false);
        }
      })
      .catch((error) => {
        if (error?.message === 'Network Error') {
          setShowModal(true);
        } else {
          logError('Could not upload results', error);
        }
      });
  }, [
    resultBuffer,
    unloadTherapistBuffer,
    hideModalWithoutToast,
    showNotification,
  ]);

  useEffect(() => {
    if (isNetworkError) {
      setTimeout(() => setShowModal(true), 10);
    }
  }, [isNetworkError]);

  return (
    <>
      <SelectExercise
        exerciseClass={exerciseClass}
        patientId={patientId}
        setId={setId}
        exerciseSettings={exerciseSettings}
        isSelf={isSelf}
        selectedPatient={selectedPatient}
        activeSets={activeSets}
        preview={preview}
      />
      <ConfirmModal
        isVisible={showModal}
        confirmText={'Erneut versuchen'}
        contentText={'Ergebnis-Upload fehlgeschlagen. Erneut versuchen?'}
        headerText={'Upload fehlgeschlagen'}
        confirmAction={uploadResults}
        cancelText={'Später übertragen'}
        cancelAction={hideModalWithToast}
      />
    </>
  );
}
